import { useEffect, useCallback } from 'react';
import { useAppointmentHistoryStore } from '@/stores/appointments/useAppointmentHistoryStore';

interface UseAppointmentHistoryParams {
  dietitianId: string;
  patientId: string;
  pageSize?: number;
}

export type UseAppointmentHistoryReturn = {
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  totalItems: number;
  items: ReturnType<typeof useAppointmentHistoryStore.getState>['items'];
  goToPage: (page: number) => Promise<void>;
  refresh: () => Promise<void>;
};

export function useAppointmentHistory({ dietitianId, patientId, pageSize = 10 }: UseAppointmentHistoryParams): UseAppointmentHistoryReturn {
  const loading = useAppointmentHistoryStore((s) => s.loading);
  const error = useAppointmentHistoryStore((s) => s.error);
  const page = useAppointmentHistoryStore((s) => s.page);
  const pageSizeState = useAppointmentHistoryStore((s) => s.pageSize);
  const totalItems = useAppointmentHistoryStore((s) => s.totalItems);
  const items = useAppointmentHistoryStore((s) => s.items);
  const load = useAppointmentHistoryStore((s) => s.load);
  const refreshStore = useAppointmentHistoryStore((s) => s.refresh);
  const reset = useAppointmentHistoryStore((s) => s.reset);

  useEffect(() => {
    reset();
    void load(dietitianId, patientId, 1, pageSize);
  }, [reset, load, dietitianId, patientId, pageSize]);

  const goToPage = useCallback(
    async (page: number) => {
      await load(dietitianId, patientId, page, pageSize);
    },
    [load, dietitianId, patientId, pageSize]
  );

  const refresh = useCallback(async () => {
    await refreshStore();
  }, [refreshStore]);

  return {
    loading,
    error,
    page,
    pageSize: pageSizeState,
    totalItems,
    items,
    goToPage,
    refresh,
  };
}

export default useAppointmentHistory;
